import { useMemo } from "react";
import {
  FilterMultiSelect,
  type MultiSelectOption,
} from "@/components/ui/filter-multi-select";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  deriveFacetOptions,
  isFiltersEmpty,
  type CalendarFilters,
  type EventFacetKey,
  type FacetKey,
} from "../lib/calendar-filters";
import type { CalendarEvent } from "../types";

/** Display config for each event-sourced facet. */
const FACETS: {
  key: EventFacetKey;
  label: string;
  singular: string;
  search: string;
  empty: string;
}[] = [
  { key: "teachers", label: "Teacher", singular: "teacher", search: "Search teachers…", empty: "No teachers in view" },
  { key: "rooms", label: "Room", singular: "room", search: "Search rooms…", empty: "No rooms in view" },
  { key: "courses", label: "Course", singular: "course", search: "Search courses…", empty: "No courses in view" },
  { key: "subjects", label: "Subject", singular: "subject", search: "Search subjects…", empty: "No subjects in view" },
];

interface CalendarFilterBarProps {
  /** Unfiltered loaded (windowed) events — the source for facet option lists. */
  events: CalendarEvent[];
  filters: CalendarFilters;
  onToggle: (key: FacetKey, value: string) => void;
  onRemove: (key: FacetKey, value: string) => void;
  onClearAll: () => void;
  /** Event counts for the live-region announcement + empty affordance. */
  totalCount: number;
  visibleCount: number;
  /** Student options from the run's assignments (value=identifier, label=name). */
  studentOptions: MultiSelectOption[];
  /** Disable the student facet (no run yet / run still solving). */
  studentDisabled?: boolean;
  studentEmptyHint?: string;
  /** Grade options from the run's placed+graded students (value="11", label="Grade 11"). */
  gradeOptions: MultiSelectOption[];
  /** Disable the grade facet (no run yet / no graded roster). */
  gradeDisabled?: boolean;
  gradeEmptyHint?: string;
  /** Normalized course code → friendly title, so the Course facet shows names. */
  courseNames?: ReadonlyMap<string, string>;
}

/**
 * Inline filter bar above the calendar grid. Renders one multi-select per
 * event-sourced facet (options derived from the loaded window) plus a Student
 * facet (options from the run's assignments), the active selections as
 * removable chips, and a "Clear all". An aria-live region announces the visible
 * event count so the otherwise-silent grid change reaches screen readers.
 * Review-only — this never touches the export path.
 */
export function CalendarFilterBar({
  events,
  filters,
  onToggle,
  onRemove,
  onClearAll,
  totalCount,
  visibleCount,
  studentOptions,
  studentDisabled = false,
  studentEmptyHint = "No students",
  gradeOptions,
  gradeDisabled = false,
  gradeEmptyHint = "No grades",
  courseNames,
}: CalendarFilterBarProps) {
  const options = useMemo(
    () => deriveFacetOptions(events, courseNames),
    [events, courseNames],
  );
  const hasFilters = !isFiltersEmpty(filters);

  // Chip labels: courses show their name (from the complete sections map, so an
  // off-window pick still reads by name), students and grades read from their
  // option lists (so the label format lives only in the option builder and a
  // chip can't drift from its dropdown row); the other facets are self-labelling.
  const chipLabel = (key: FacetKey, value: string): string => {
    if (key === "courses") return courseNames?.get(value) ?? value;
    if (key === "students") {
      return studentOptions.find((o) => o.value === value)?.label ?? value;
    }
    if (key === "grades") {
      return gradeOptions.find((o) => o.value === value)?.label ?? value;
    }
    return value;
  };

  // The grade chip label already reads "Grade 11", so its remove-label uses the
  // bare value ("Remove grade 11 filter") to avoid the "Remove grade Grade 11"
  // stutter the generic `Remove {singular} {label}` template would produce.
  const removeLabel = (chip: {
    key: FacetKey;
    value: string;
    label: string;
    singular: string;
  }): string =>
    chip.key === "grades"
      ? `Remove grade ${chip.value} filter`
      : `Remove ${chip.singular} ${chip.label} filter`;

  // Grade chips flow through the same list as every other facet — the page is
  // responsible for pruning stale grades out of `filters` (so the grid and the
  // chip bar agree), so every value reaching here maps to a live selection.
  const chips: {
    key: FacetKey;
    value: string;
    label: string;
    singular: string;
  }[] = [
    ...FACETS.flatMap((f) =>
      filters[f.key].map((value) => ({
        key: f.key,
        value,
        label: chipLabel(f.key, value),
        singular: f.singular,
      })),
    ),
    ...filters.students.map((value) => ({
      key: "students" as FacetKey,
      value,
      label: chipLabel("students", value),
      singular: "student",
    })),
    ...filters.grades.map((value) => ({
      key: "grades" as FacetKey,
      value,
      label: chipLabel("grades", value),
      singular: "grade",
    })),
  ];

  return (
    <div data-testid="calendar-filter-bar" className="mb-3 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {FACETS.map((f) => (
          <FilterMultiSelect
            key={f.key}
            label={f.label}
            options={options[f.key]}
            selected={filters[f.key]}
            onToggle={(value) => onToggle(f.key, value)}
            emptyHint={f.empty}
            searchPlaceholder={f.search}
            data-testid={`calendar-filter-${f.key}`}
          />
        ))}

        <FilterMultiSelect
          label="Student"
          options={studentOptions}
          selected={filters.students}
          onToggle={(value) => onToggle("students", value)}
          disabled={studentDisabled}
          emptyHint={studentEmptyHint}
          searchPlaceholder="Search students…"
          data-testid="calendar-filter-students"
        />

        <FilterMultiSelect
          label="Grade"
          options={gradeOptions}
          selected={filters.grades}
          onToggle={(value) => onToggle("grades", value)}
          disabled={gradeDisabled}
          emptyHint={gradeEmptyHint}
          searchPlaceholder="Search grades…"
          data-testid="calendar-filter-grades"
        />

        {hasFilters && (
          <button
            type="button"
            onClick={onClearAll}
            data-testid="calendar-filter-clear-all"
            className="rounded-md px-2 py-1.5 text-xs font-medium text-text-muted hover:text-white hover:bg-surface-2/60 focus:outline-none focus:ring-2 focus:ring-brand"
          >
            Clear all
          </button>
        )}

        <span
          role="status"
          // Only announce while filtering — otherwise plain week-to-week nav
          // (which changes totalCount) would chatter at screen readers.
          aria-live={hasFilters ? "polite" : "off"}
          data-testid="calendar-filter-count"
          className="ml-auto text-xs text-text-muted"
        >
          {hasFilters
            ? `Showing ${visibleCount} of ${totalCount} events`
            : `${totalCount} events`}
        </span>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map((c) => (
            <FilterChip
              key={`${c.key}:${c.value}`}
              label={c.label}
              removeLabel={removeLabel(c)}
              onRemove={() => onRemove(c.key, c.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
